import React, { useEffect, useCallback, useRef } from "react";

import { useGoogleTagManager } from "~/hooks/useGoogleTagManager";

export interface AnalyticsCreativeSlotProps
  extends React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  > {
  /**
   * Name of the creative slot.
   * @example "Homepage Hero - Slide 1"
   */
  name: string;
  /** Children where links will be tracked */
  children: React.ReactNode;
}

/**
 * This component wraps a "creative slot", intercepting click events emitted within
 * it to emit "select_promotion" events to Google Analytics.
 *
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#select_promotion
 */
export function AnalyticsCreativeSlot({
  children,
  name: creativeSlotName,
  ...props
}: AnalyticsCreativeSlotProps) {
  const googleTagManager = useGoogleTagManager();

  const onClick = useCallback(
    (event: MouseEvent) => {
      // Check if this click has already been processed by a child AnalyticsCreativeSlot
      // @ts-ignore - Using Symbol as a property
      if (event[PROCESSED_SYMBOL]) return;

      // Get the target from the click event (this could be a child of the link).
      const target = event.target;
      if (!(target instanceof Element)) return;

      // Find the actual link which was clicked.
      const link =
        target instanceof HTMLAnchorElement ? target : target.closest("a");
      if (!link) return;

      /**
       * Get the promotion name from a variety of potential places.
       * @example "New Boot Obsessions"
       */
      const promotionName =
        link.dataset.promoName ||
        link.title?.trim() ||
        link.innerText?.trim() ||
        link.href;

      // Cannot continue without a promotion name.
      if (!promotionName) {
        console.warn("Promotion name could not be resolved from link", link);
        return;
      }

      // Mark this event as processed to prevent parent slots from handling it
      // @ts-ignore - Using Symbol as a property
      event[PROCESSED_SYMBOL] = true;

      // Emit the select_promotion event.
      googleTagManager.push({
        event: "select_promotion",
        properties: {
          promotion_name: promotionName,
          creative_slot: creativeSlotName,
        },
      });
    },
    [googleTagManager],
  );

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    node.addEventListener("click", onClick);
    return () => node.removeEventListener("click", onClick);
  }, [onClick]);

  return (
    <div {...props} ref={ref}>
      {children}
    </div>
  );
}

// Symbol used as a marker on elements that have been processed by an AnalyticsCreativeSlot
const PROCESSED_SYMBOL = Symbol("analytics-creative-slot-processed");
